import { Modal, Tooltip } from 'antd';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import { RefundableEnum } from '@common-types';
import { RootState, formatDateCheckInOut, isSpanish, renderNumber } from '@share/utils';
import { es } from 'date-fns/esm/locale';
import { Currency } from '@components';
import { DATE_FORMAT_CHECK_IN_OUT } from '@constants';
import { PeopleSvg, SuitecaseSvg, TransmitionSvg, AirSvg } from '@assets';

import { useQuoteCarModalCompare } from './useQuoteCarModalCompare';

import './style.scss';

const modalWidth = 900;
const percents = 100;

interface IProps {
  visible: boolean;
  quoteBase: string;
  onCancel: () => void;
}

export function QuoteCarModalCompare(props: IProps) {
  const { visible, quoteBase, onCancel } = props;
  const quoteStore = useSelector((state: RootState) => state.quoteStore);
  const loginStore = useSelector((state: RootState) => state.loginStore);

  const {
    myQuotes,
    isHistorical,
    fontFamily,
    secondaryFontFamily,
    handleView,
    modalOpen,
  } = useQuoteCarModalCompare({ quoteStore, loginStore, quoteBase });

  return (
    <Modal
      className={`modal-compare ${fontFamily ? `${fontFamily}-font` : ''} ${secondaryFontFamily ? `secondary-${secondaryFontFamily}-font` : ''}`}
      open={visible && modalOpen}
      footer={null}
      onCancel={onCancel}
      wrapClassName="modal-compare__wrapper"
      width={modalWidth}
    >
      <div>
        <div className="modal-compare__title-row">
          <h4 className="modal-compare__title secondary-font">
            <FormattedMessage id="results.car.card.compare.modal.title" />
          </h4>
        </div>

        <div className="modal-compare__hotels modal-compare__hotels--meta">
          <div className="hotels-dates">
            {myQuotes[0]?.checkIn
              ? formatDateCheckInOut(myQuotes[0]?.checkIn, DATE_FORMAT_CHECK_IN_OUT, isSpanish() ? es : undefined)
              : 'NA'}{' '}
            -{' '}
            {myQuotes[0]?.checkOut
              ? formatDateCheckInOut(myQuotes[0]?.checkOut, DATE_FORMAT_CHECK_IN_OUT, isSpanish() ? es : undefined)
              : 'NA'}
          </div>
          <div className="hotels-dates">{myQuotes[0]?.occupancy}</div>
        </div>

        <div className="modal-compare__hotels">
          {myQuotes.map((quote, index) => {
            let amenitiesObj: Record<string, string> = {};
            const amenities = quote?.amenities;

            if (amenities && quote.quoteType === 'Car') {
              if (typeof amenities === 'object') {
                try {
                  amenitiesObj = JSON.parse(amenities as unknown as string);
                } catch {
                  amenitiesObj = {};
                }
              }
            }

            const savings =
              quote.publicPrice && quote.price && quote.publicPrice > 0
                ? Math.floor(percents - (quote.price * percents) / quote.publicPrice)
                : 0;
            const logoUrl = quote.supplierLogo || '';

            return (
              <div key={index} className="modal-compare__hotel">
                <div className="modal-compare__media">
                  <div className="hotel-images-container hotelImageHeight">
                    <div className="hotel-image" style={{ backgroundImage: `url(${quote.image})` }} />
                    {savings > 0 ? (
                      <div className="modal-compare__savings-badge">
                        {savings.toFixed(0)} % <FormattedMessage id="results.cars.card.compare.modal.off" />
                      </div>
                    ) : null}
                  </div>
                </div>

                <div className="hotel-text-container" style={{ minHeight: '20px', height: 'auto' }}>
                  <p className="mb-0">
                    {logoUrl ? (
                      <div className="cars-content-location__vendor">
                        <img alt="Vendor logo" src={logoUrl} style={{ maxWidth: '100%', height: 'auto' }} />
                      </div>
                    ) : (
                      <span>{quote.displayName || '-'}</span>
                    )}
                  </p>
                </div>

                <div className="hotel-text-container" style={{ minHeight: '20px', height: 'auto' }}>
                  <p className="mb-0">
                    <Tooltip title={quote.displayName} placement="top">
                      {quote.displayName}
                    </Tooltip>
                  </p>
                </div>

                <div className="hotel-text-container hotel-text-container--car-location">
                  <Tooltip title={quote.pickUpLocationDetail} placement="top">
                    <p>{quote.pickUpLocationDetail}</p>
                  </Tooltip>
                </div>

                <div className="hotel-text-container" style={{ minHeight: '50px', height: 'auto' }}>
                  <div className="cars-card__item">
                    <TransmitionSvg />
                    <label className="ms-1">{amenitiesObj?.transmission}</label>
                  </div>
                  <div className="cars-card__item">
                    <PeopleSvg />
                    <label className="ms-1">
                      <FormattedMessage id="cars.filter.seats" />: {amenitiesObj?.seats}
                    </label>
                  </div>
                  <div className="cars-card__item">
                    <SuitecaseSvg />
                    <label className="ms-1">
                      <FormattedMessage id="adult" values={{ count: amenitiesObj?.seats }} />: {amenitiesObj?.seats}
                    </label>
                  </div>
                  <div className="cars-card__item">
                    <SuitecaseSvg />
                    <label className="ms-1">
                      <FormattedMessage id="cars.baggage" />: {amenitiesObj?.luggage}
                    </label>
                  </div>
                  <div className="cars-card__item">
                    <AirSvg />
                    <label className="ms-1">
                      <FormattedMessage id="cars.ac" />
                    </label>
                  </div>
                </div>

                {quote.refundability && quote.refundability !== RefundableEnum.Unknown ? (
                  <div className="hotel-text-container hotel-text-container--refundable">
                    <div className="hotel-card-wrapper__refundable">
                      {quote.refundability === RefundableEnum.Refundable && (
                        <div className="hotel-card-wrapper__refundable-ref">
                          <FormattedMessage id="refundable" />
                        </div>
                      )}
                      {quote.refundability === RefundableEnum.Nonrefundable && (
                        <div className="hotel-card-wrapper__refundable-nonref">
                          <FormattedMessage id="non.refundable" />
                        </div>
                      )}
                    </div>
                  </div>
                ) : null}

                <div
                  className={`hotel-text-container hotel-text-container--price${
                    isHistorical ? ' hotel-text-container--flush-bottom' : ''
                  }`}
                >
                  {quote.publicPrice ? (
                    <p className="mb-0" style={{ textDecoration: 'line-through' }}>
                      <Currency currency={quote.currency} />
                      {renderNumber(quote.publicPrice, 2)}
                    </p>
                  ) : null}
                  <p className="mb-0">
                    <strong>
                      <Currency currency={quote.currency} />
                      {renderNumber(quote.price, 2)}
                    </strong>
                  </p>
                </div>

                {!isHistorical ? (
                  <div className="hotel-view-container">
                    <div className="view-button" onClick={() => handleView(quote)}>
                      <FormattedMessage id="results.cars.card.compare.modal.view" />
                    </div>
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      </div>
    </Modal>
  );
}
